import { ROLE_PERMISSIONS } from './constants';

/**
 * Permission utility functions for role-based access control
 * Now supports ViewAs functionality - uses effectiveUser when available
 */

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with role property (can be effectiveUser from ViewAs)
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[user.role];
  return rolePermissions ? rolePermissions[permission] || false : false;
};

/**
 * Check if user can create events
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canCreateEvents = (user) => {
  return hasPermission(user, 'canCreateEvents');
};

/**
 * Check if user can edit events
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canEditEvents = (user) => {
  return hasPermission(user, 'canEditEvents');
};

/**
 * Check if user can create other users
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canCreateUsers = (user) => {
  return hasPermission(user, 'canCreateUsers');
};

/**
 * Check if user can create admin users
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canCreateAdmins = (user) => {
  return hasPermission(user, 'canCreateAdmins');
};

/**
 * Check if user can delete any event
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canDeleteAnyEvent = (user) => {
  return hasPermission(user, 'canDeleteAnyEvent');
};

/**
 * Check if user can delete any user
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canDeleteAnyUser = (user) => {
  return hasPermission(user, 'canDeleteAnyUser');
};

/**
 * Check if user can see all events
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canSeeAllEvents = (user) => {
  return hasPermission(user, 'canSeeAllEvents');
};

/**
 * Check if user can see all users
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canSeeAllUsers = (user) => {
  return hasPermission(user, 'canSeeAllUsers');
};

/**
 * Check if user can import/export attendees
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canImportExport = (user) => {
  // Both event_admin and super_admin can import/export
  return isEventAdmin(user) || isSuperAdmin(user);
};

/**
 * Check if user is super admin
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isSuperAdmin = (user) => {
  return user?.role === 'super_admin';
};

/**
 * Check if user is event admin
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isEventAdmin = (user) => {
  return user?.role === 'event_admin';
};

/**
 * Check if user is admin (super admin or event admin)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return isSuperAdmin(user) || isEventAdmin(user);
};

/**
 * Get allowed roles that a user can create
 * @param {Object} user - User object
 * @returns {Array} Array of role strings
 */
export const getAllowedRolesToCreate = (user) => {
  if (isSuperAdmin(user)) {
    return ['super_admin', 'event_admin', 'usher', 'front_desk', 'attendee'];
  } else if (isEventAdmin(user)) {
    return ['event_admin', 'usher', 'front_desk', 'attendee'];
  }
  return [];
};

/**
 * Check if user can delete a specific user
 * @param {Object} currentUser - Current logged in user
 * @param {Object} targetUser - User to be deleted
 * @returns {boolean}
 */
export const canDeleteUser = (currentUser, targetUser) => {
  if (isSuperAdmin(currentUser)) {
    return currentUser.id !== targetUser.id; // Can't delete self
  }
  
  if (isEventAdmin(currentUser)) {
    return targetUser.created_by === currentUser.id 
      && !isSuperAdmin(targetUser) 
      && !isEventAdmin(targetUser)
      && currentUser.id !== targetUser.id;
  }
  
  return false;
};

/**
 * Check if user can edit a specific user
 * @param {Object} currentUser - Current logged in user
 * @param {Object} targetUser - User to be edited
 * @returns {boolean}
 */
export const canEditUser = (currentUser, targetUser) => {
  // Same logic as delete for now
  return canDeleteUser(currentUser, targetUser) || currentUser.id === targetUser.id;
};

/**
 * Get navigation items based on user role
 * @param {Object} user - User object
 * @returns {Array} Array of navigation items
 */
export const getNavigationItems = (user) => {
  const baseItems = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'BarChart3' },
  ];

  // Events page is accessible to all staff (admins, event admins, front desk, ushers)
  // Front desk can view events (copy links, download QR) but cannot create/edit/delete
  if (user && user.role !== 'attendee') {
    baseItems.push({ id: 'events', label: 'Events', path: '/events', icon: 'Calendar' });
  }

  if (canCreateUsers(user)) {
    baseItems.push({ id: 'users', label: 'User Management', path: '/users', icon: 'Users' });
  }

  // Add other conditional menu items based on permissions
  baseItems.push(
    { id: 'attendees', label: 'Attendees', path: '/attendees', icon: 'UserCheck' },
    { id: 'check-in', label: 'Check-in', path: '/check-in', icon: 'CheckCircle' },
    { id: 'print-station', label: 'Print Station', path: '/print-station', icon: 'Printer' }
  );

  // Custom Fields - only for admins and super admins
  if (isAdmin(user) || isSuperAdmin(user)) {
    baseItems.push({ id: 'custom-fields', label: 'Custom Fields', path: '/custom-fields', icon: 'Settings' });
  }

  // Badge Templates, Stickers and Analytics - for all authenticated users
  baseItems.push(
    { id: 'badges', label: 'Badge Templates', path: '/badges', icon: 'Award' },
    { id: 'stickers', label: 'Sticker Templates', path: '/stickers', icon: 'Palette' },
    { id: 'analytics', label: 'Analytics', path: '/analytics', icon: 'TrendingUp' }
  );

  if (isSuperAdmin(user)) {
    baseItems.push({ id: 'system-settings', label: 'System Settings', path: '/system-settings', icon: 'Cog' });
  }

  return baseItems;
};
